// Phase 3 (grade-2/3 ceiling) — `fraction_bar` concept visual for fractions.
//
// Renders a whole BAR split into `b` equal parts with `a` of them shaded —
// the canonical area model for a fraction a/b (e.g. 1/2, 1/4, 3/4). ADDITIVE
// — registers itself under the "fraction_bar" kind (see rendererRegistry); it
// plugs in with NO edit to MathVisual or the session screens (the registry
// resolves it by key), exactly like array / place_value_blocks.
//
// READS its visualData via the shared MathRendererProps contract (same a/b
// channel ArrayRenderer uses):
//   • numerator (shaded parts) ← `a`
//   • denominator (total equal parts) ← `b`
// FAIL-SAFE: a non-finite / missing / nonsensical payload degrades to a
// single unshaded whole (b=1, a=0) rather than rendering nothing or NaN.
// The denominator is clamped to a sane max (12) so an oversized authored `b`
// can't emit a runaway number of segments / a degenerate viewBox; the
// numerator is clamped to 0..b so an over-large `a` can't shade past the
// whole.
import type { MathRendererProps } from "./rendererRegistry";

const SHADED = "var(--pal-green, #5a9e6f)";
const EMPTY = "var(--color-bg-secondary, #f5efe3)";
const STROKE = "var(--color-border-secondary, #cdbfae)";

const MAX_DENOM = 12; // clamp: keeps segment count + viewBox sane
const BAR_W = 320; // px
const BAR_H = 64; // px
const PAD = 2; // outer padding so the stroke isn't clipped

function FractionBarRenderer({ a, b }: MathRendererProps) {
  // Denominator: at least 1 equal part, at most MAX_DENOM. A non-finite or
  // <1 value degrades to a single whole (never a divide-by-zero / NaN width).
  const denom = Number.isFinite(b as number)
    ? Math.max(1, Math.min(MAX_DENOM, Math.floor(b as number)))
    : 1;
  // Numerator: 0..denom shaded parts. Non-finite / negative → 0 (unshaded
  // whole); over-large → the whole bar (clamped, never past the whole).
  const num = Number.isFinite(a as number)
    ? Math.max(0, Math.min(denom, Math.floor(a as number)))
    : 0;

  const segW = BAR_W / denom;
  const segments = Array.from({ length: denom });

  return (
    <svg
      viewBox={`0 0 ${BAR_W + PAD * 2} ${BAR_H + PAD * 2}`}
      width="100%"
      style={{ maxWidth: Math.min(360, BAR_W + PAD * 2) }}
      role="img"
      aria-label={`Fraction bar: ${num} of ${denom} equal parts shaded`}
    >
      {segments.map((_, i) => (
        <rect
          key={i}
          x={PAD + i * segW}
          y={PAD}
          width={segW}
          height={BAR_H}
          fill={i < num ? SHADED : EMPTY}
          stroke={STROKE}
          strokeWidth="2"
        />
      ))}
    </svg>
  );
}

export default FractionBarRenderer;
